/**
 * Login View
 *
 * Displays the login page with Azure AD sign-in button.
 * Shows loading state during authentication.
 * Displays error messages if login fails.
 */

import { renderIcon, createSpinner } from '../utils/icons';
import { login } from '../services/auth-service';
import { subscribeToAuth, type AuthState, selectAuthError } from '../services/auth-store';

/**
 * Create the login view element
 */
export function createLoginView(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'min-h-screen flex items-center justify-center p-4';

  // Create glass card container
  const card = document.createElement('div');
  card.className = 'glass-card w-full max-w-md p-8 rounded-2xl';

  // Logo/Brand section
  const brandSection = document.createElement('div');
  brandSection.className = 'text-center mb-8';
  brandSection.innerHTML = `
    <div class="flex justify-center mb-4">
      <div class="w-16 h-16 rounded-xl bg-primary/20 flex items-center justify-center">
        ${renderIcon('briefcase', { size: 'xl', className: 'text-primary' }).outerHTML}
      </div>
    </div>
    <h1 class="text-2xl font-bold text-foreground mb-2">Wyden Expenditure</h1>
    <p class="text-muted-foreground">Approval Request System</p>
  `;

  // Login form section
  const formSection = document.createElement('div');
  formSection.className = 'space-y-6';

  // Error message container (hidden by default)
  const errorContainer = document.createElement('div');
  errorContainer.id = 'login-error';
  errorContainer.className = 'hidden p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm';
  errorContainer.setAttribute('role', 'alert');

  // Login button
  const loginButton = document.createElement('button');
  loginButton.type = 'button';
  loginButton.id = 'login-button';
  loginButton.className = `
    w-full flex items-center justify-center gap-3 px-6 py-3
    bg-primary text-primary-foreground font-medium
    rounded-lg transition-all duration-200
    hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/25
    focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background
    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none
  `.replace(/\s+/g, ' ').trim();

  // Button content
  const buttonContent = document.createElement('span');
  buttonContent.className = 'flex items-center gap-3';
  buttonContent.innerHTML = `
    ${renderIcon('logIn', { size: 'sm' }).outerHTML}
    <span>Sign in with Microsoft</span>
  `;
  loginButton.appendChild(buttonContent);

  // Loading spinner (hidden by default)
  const spinner = createSpinner({ size: 'sm' });
  spinner.id = 'login-spinner';
  spinner.classList.add('hidden');

  // Loading text
  const loadingText = document.createElement('span');
  loadingText.id = 'login-loading-text';
  loadingText.className = 'hidden';
  loadingText.textContent = 'Redirecting to Microsoft...';

  loginButton.appendChild(spinner);
  loginButton.appendChild(loadingText);

  // Handle login click
  loginButton.addEventListener('click', handleLoginClick);

  // Info section
  const infoSection = document.createElement('div');
  infoSection.className = 'text-center text-sm text-muted-foreground';
  infoSection.innerHTML = `
    <p class="flex items-center justify-center gap-2">
      ${renderIcon('shield', { size: 'xs', className: 'text-success' }).outerHTML}
      <span>Secured by Microsoft Azure AD</span>
    </p>
  `;

  // Assemble form section
  formSection.appendChild(errorContainer);
  formSection.appendChild(loginButton);

  // Divider
  const divider = document.createElement('div');
  divider.className = 'relative my-6';
  divider.innerHTML = `
    <div class="absolute inset-0 flex items-center">
      <div class="w-full border-t border-border"></div>
    </div>
  `;

  // Assemble card
  card.appendChild(brandSection);
  card.appendChild(formSection);
  card.appendChild(divider);
  card.appendChild(infoSection);

  container.appendChild(card);

  // Subscribe to auth state changes for error display
  const unsubscribe = subscribeToAuth((state: AuthState) => {
    const error = selectAuthError(state);
    updateErrorDisplay(errorContainer, error);
  });

  // Store unsubscribe function for cleanup
  (container as HTMLElement & { cleanup?: () => void }).cleanup = unsubscribe;

  return container;
}

/**
 * Handle login button click
 */
async function handleLoginClick(event: MouseEvent): Promise<void> {
  event.preventDefault();

  const button = document.getElementById('login-button') as HTMLButtonElement | null;
  const buttonContent = button?.querySelector('span:first-child') as HTMLSpanElement | null;
  const spinner = document.getElementById('login-spinner');
  const loadingText = document.getElementById('login-loading-text');
  const errorContainer = document.getElementById('login-error');

  if (!button) return;

  // Hide error
  if (errorContainer) {
    errorContainer.classList.add('hidden');
  }

  // Show loading state
  button.disabled = true;
  if (buttonContent) buttonContent.classList.add('hidden');
  if (spinner) spinner.classList.remove('hidden');
  if (loadingText) loadingText.classList.remove('hidden');

  try {
    await login();
    // If we get here without redirect, something went wrong
    // login() should redirect, so this code shouldn't execute normally
  } catch (error) {
    // Reset button state
    button.disabled = false;
    if (buttonContent) buttonContent.classList.remove('hidden');
    if (spinner) spinner.classList.add('hidden');
    if (loadingText) loadingText.classList.add('hidden');

    // Show error
    if (errorContainer) {
      const message = error instanceof Error ? error.message : 'An error occurred during login';
      errorContainer.textContent = message;
      errorContainer.classList.remove('hidden');
    }
  }
}

/**
 * Update error display
 */
function updateErrorDisplay(container: HTMLElement, error: string | null): void {
  if (error) {
    container.textContent = error;
    container.classList.remove('hidden');
  } else {
    container.classList.add('hidden');
  }
}

/**
 * Login view renderer for router
 */
export function loginView(): HTMLElement {
  return createLoginView();
}

export default loginView;
